import { DialogBase } from '@/components/dialog';
import { GeneralTable } from '@/components/tables/general';
import { Button } from '@/components/ui/button';
import { formatNumber } from '@/utils/numberFormat';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInsuranceStatsBreakdown } from '../hooks';
import { InsuranceBreakdownItem, InsuranceMetric } from '../model';

interface InsuranceMetricDialogProps {
  metric: InsuranceMetric;
  title: string;
  year?: string | number;
  plantId?: string | number;
  closeDialog: () => void;
}

/** Karta strelkasi bosilganda ochiladi: tanlangan ko'rsatkich viloyatlar
 *  kesimida, viloyat qatoriga bosilsa — o'sha viloyatning tumanlari. */
export const InsuranceMetricDialog = ({
  metric,
  title,
  year,
  plantId,
  closeDialog,
}: InsuranceMetricDialogProps) => {
  const { t } = useTranslation();
  const [region, setRegion] = useState<{ code: number; name: string }>();

  const { data, isLoading } = useInsuranceStatsBreakdown({
    metric,
    year,
    region_code: region?.code,
    plant_id: plantId,
  });

  const rows = useMemo(
    () =>
      (data?.items || [])
        .slice()
        .sort((a, b) => Number(b.value) - Number(a.value)),
    [data],
  );

  const columns = useMemo<ColumnDef<InsuranceBreakdownItem>[]>(
    () => [
      {
        accessorKey: 'region_name',
        header: () => t(region ? 'district_name' : 'region_name'),
        /** `region_code` bilan so'ralganda tuman nomi keladi. */
        cell: ({ row }) =>
          row.original.region_name || row.original.district_name || '-',
        footer: () => t('total'),
      },
      {
        accessorKey: 'value',
        header: () => title,
        cell: ({ row }) => formatNumber(Number(row.original.value)),
        footer: () =>
          formatNumber(
            rows.reduce((sum, item) => sum + Number(item.value || 0), 0),
          ),
      },
    ],
    [region, rows, t, title],
  );

  return (
    <DialogBase
      title={
        <>
          {region && (
            <Button
              variant="outline"
              className="size-7 shrink-0 rounded-md border-white/20 bg-card p-0"
              onClick={() => setRegion(undefined)}
            >
              <ArrowLeft className="size-4 text-violet-600" />
            </Button>
          )}
          <span>{region ? `${title} — ${region.name}` : title}</span>
        </>
      }
      onClose={closeDialog}
      size="lg"
    >
      {/* Sarlavha bilan jadval orasida bo'shliq. */}
      <div className="mt-4 flex flex-col gap-3">
        <GeneralTable
          data={rows}
          columns={columns}
          loading={isLoading}
          /** Viloyat qatoriga bosilsa tumanlar kesimiga tushamiz. */
          onRowClick={
            region
              ? undefined
              : (row: InsuranceBreakdownItem) =>
                  row.region_code &&
                  setRegion({
                    code: row.region_code,
                    name: row.region_name || '',
                  })
          }
        />
      </div>
    </DialogBase>
  );
};
